import { Grid, Stack, Typography, useTheme } from "@mui/material";
import { MprcJoinComponents, MprcLink, MprcRequestErrorReport, MprcSpinnerIndicator, MprcText } from "components";
import { MprcFeedTypography } from "components/MprcFeedTypography";
import { MprcPollAvailable, MprcPollComputed } from "components/MprcPoll";
import RequestErrorUtils from "components/MprcRequestErrorReport";
import { FeedPollResults, FeedType, PollItemModel, PublicationApprovalStatus, UserItemDtoPaginatedList } from "generated/CoreApi_v1";
import { Formaters } from "lib/Formaters";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import { useSession } from "providers/SessionProvider";
import React, { useCallback, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router";
import { KnowledgePaths } from "routes/KnowledgeRouter";
import { ListUsersDrawer } from "views/foundation/user/drawers/ListUsersDrawer";
import { ModeratorMessageButton } from "../../actions/ModeratorMessageDialog";
import { useCardContext } from "../../CardBuilder";
import { DefaultModerationFooter } from "../common/DefaultModerationFooter";
import { DefaultOptions } from "../common/DefaultOptions";
import { CommentButton, FavoriteButton, LikeButton, ShareButton } from "../common/Interaction";
import { StickerBox } from "../common/StickerBox";

type ViewMode = "toAnswer" | "seeAnswers";

////////////////////////////////////////////////////////////////////////////////

export const DefaultTitle = () => {
    const { pub, otherInfo } = useCardContext();
    const theme = useTheme();
    const { t } = useTranslation("knowledge");
    const navigate = useNavigate();
    const location = useLocation();

    const isBlocked = pub.blocked?.blockedOn != undefined;
    var borderStyle: React.CSSProperties | undefined = otherInfo?.feedType != FeedType.ContentPage && (pub.interaction.isNewPublication || pub.blocked?.firstAccessAfterBlock === true || pub.rejected?.firstAccessAfterRejected === true)
        ? { borderLeft: "4px solid", borderLeftColor: theme.palette.warning.main, paddingLeft: theme.spacing(1) }
        : undefined;

    const navigateUrl = KnowledgePaths.PublicationViewerResolver(pub.publicationId, pub.publicationType, pub.masterPublication?.publicationId);

    return (
        <div style={borderStyle}>
            {
                navigateUrl == document.location.pathname
                    ? (
                        <MprcFeedTypography variant="title" component="div" color={isBlocked ? "warning" : undefined}>
                            <span title={pub.title}>{pub.title}</span>
                        </MprcFeedTypography>
                    )
                    : (
                        <Link
                            onClick={() => navigate(document.location.search, { replace: true, state: { ...(location.state ?? {}), publicationId: pub.publicationId } })}
                            to={navigateUrl}
                        >
                            <MprcFeedTypography variant="title" component="div" color={isBlocked ? "warning" : undefined}>
                                <span title={pub.title}>{pub.title}</span>
                            </MprcFeedTypography>
                        </Link>
                    )
            }

            <Stack spacing={0.5} alignItems="flex-end" direction="row">
                {pub.sticker && <StickerBox name={pub.sticker.name} textColor={pub.sticker.textColor} backgroundColor={pub.sticker.backgroundColor} />}
                {isBlocked && <StickerBox name={t("Feed.Card.Common.BlockedText")} textColor={theme.palette.common.white} backgroundColor={theme.palette.warning.main} />}
            </Stack>
        </div>
    );
};

export const DefaultContent = () => {

    const { pub: { publicationId, title, pollDefinition, author, readOnly, moderateReport } } = useCardContext();
    const { answerOptions, allowAnswersUntil, allowNewAnswers, anonymousAnswers } = pollDefinition!;
    const { t } = useTranslation("knowledge");
    const { enqueueSnackbar } = useSnackbar();

    const [dialog, setDialog] = useState<any>();
    const [error, setError] = useState<any>(undefined);
    const [loading, setLoading] = useState<boolean>(false);
    const [results, setResults] = useState<FeedPollResults | null>(pollDefinition != undefined ? pollDefinition.results : null);
    const [selectedAnswerId, setSelectedAnswerId] = useState<string | null | undefined>(pollDefinition != undefined ? pollDefinition.selectedAnswerId : null);
    const [viewMode, setViewMode] = useState<ViewMode>(pollDefinition?.selectedAnswerId == undefined && allowNewAnswers && !readOnly && moderateReport == null ? "toAnswer" : "seeAnswers");

    const api = useCoreApi();
    const session = useSession();

    ////////////////////////////////////////////////////////////////////////////////

    ///
    const handleSelectOption = (answerId: string) => {
        setLoading(true);
        api.v1.knowledgePublication.addPollAnswerCommand({ publicationId, answerId })
            .then(result => {
                if (result.data != undefined) {
                    setResults(result.data);
                    setSelectedAnswerId(answerId);
                    setViewMode("seeAnswers");
                }
                else {
                    enqueueSnackbar(t("Feed.Card.Poll.SaveAnswerFailMessage"));
                }
            }).catch((e) => {

                const errorMessage =
                    RequestErrorUtils.findValidationMessage(e, "publicationId", "PublicationNotFound") ||
                    RequestErrorUtils.findValidationMessage(e, "answerId", "PollPublicationAlreadyVoted") ||
                    RequestErrorUtils.findValidationMessage(e, "", "PollPublicationClosed");

                if (errorMessage) enqueueSnackbar(errorMessage, { variant: "error" });
                else setError(e);
            })
            .finally(() => setLoading(false));
    };

    ///
    const handleChangeView = () => {
        if (viewMode == "toAnswer") {
            api.v1.knowledgePublication.getPollResults({ publicationId })
                .then(result => {
                    setResults(result.data);
                    setViewMode("seeAnswers");
                });
        } else {
            setViewMode("toAnswer");
        }
    };

    ///
    const handleViewWhoAnswered = () => {
        setDialog(
            <ListUsersDrawer
                title={t("Feed.Card.Poll.WhoAnsweredDrawerTitle")}
                subtitle={title}
                onGetData={({ searchText, pageNumber, pageSize }) =>
                    new Promise<UserItemDtoPaginatedList>((resolve, reject) => {
                        api.v1.knowledgePublication.getWhoAnsweredPoll({
                            publicationId: publicationId,
                            searchText: searchText,
                            pageNumber: pageNumber,
                            pageSize: pageSize
                        })
                            .then(result => resolve(result.data))
                            .catch(e => reject(e));
                    })}
                onClose={() => setDialog(null)}
            />);
    };

    ////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////////

    ///
    const renderPoll = useCallback((item: PollItemModel) => {
        return (
            <Grid key={item.id} size="grow">
                <MprcPollAvailable
                    text={item.text}
                    onClick={() => handleSelectOption(item.id)}
                />
            </Grid>
        );
    }, []);

    ///
    const renderPollResults = useCallback((item: PollItemModel) => {

        let percent = 0;

        if (results?.answerResults != undefined) {
            var answerResult = results?.answerResults.find(x => x.id == item.id);

            if (results?.answerCount == 0) {
                percent = 0;
            } else if (answerResult != undefined) {
                percent = Math.round(answerResult.voteCount / results!.answerCount * 100);
            }
        }

        const getWinAnswerId = (): string | undefined => {
            let winAnswerId;
            if (results?.answerResults != undefined) {
                const winAnswerResult = results.answerResults.reduce((current, prev) => (prev.voteCount > current.voteCount) ? prev : current);

                if (results.answerResults.filter(x => x.voteCount == winAnswerResult.voteCount).length === 1) {
                    winAnswerId = winAnswerResult.id;
                }
            }
            return winAnswerId;
        };

        return (
            <Grid container key={item.id} width="100%" alignItems={"center"}>
                <Grid size="grow">
                    <MprcPollComputed
                        text={item.text}
                        selected={item.id === selectedAnswerId}
                        percent={percent}
                        highlighted={item.id === getWinAnswerId()}
                    />
                </Grid>
                <Grid sx={{ width: "45px", textAlign: "end" }}>
                    <MprcText color={item.id === getWinAnswerId() ? "success" : "inherit"}>{percent}%</MprcText>
                </Grid>
            </Grid>
        );
    }, [results, selectedAnswerId]);

    return loading
        ? <MprcSpinnerIndicator />
        : error
            ? <MprcRequestErrorReport error={error} />
            : (
                <>
                    {dialog}
                    <Grid container direction="column" spacing={1}>
                        {answerOptions.map(item =>
                            <Grid key={item.id} size={{ xs: 12 }} alignItems="center">
                                {viewMode == "toAnswer" ? renderPoll(item) : renderPollResults(item)}
                            </Grid>
                        )}
                        <Grid size={{ xs: 12 }} sx={{ textAlign: "center" }}>
                            <>
                                <Typography variant="caption" component="p">
                                    <MprcJoinComponents
                                        separator="&bull;"
                                        components={[
                                            !allowNewAnswers ? `${t("Feed.Card.Poll.PollClosedText")}` : null,
                                            allowNewAnswers && allowAnswersUntil != undefined ? `${t("Feed.Card.Poll.PollEndsText")} ${Formaters.relativeTime(allowAnswersUntil)}` : null,
                                            anonymousAnswers === true ? t("Feed.Card.Poll.AnonymousVotingText") : t("Feed.Card.Poll.IdentifiedVotesText"),
                                            results && results.answerCount > 0 ?
                                                anonymousAnswers
                                                    ? t(`knowledge:Feed.Card.Poll.${allowNewAnswers ? "PeopleAlreadyAnsweredText" : "PeopleAlreadyAnsweredClosedText"}`, { count: results.answerCount })
                                                    : <Trans
                                                        i18nKey={`knowledge:Feed.Card.Poll.${allowNewAnswers ? "PeopleAlreadyAnsweredLinkText" : "PeopleAlreadyAnsweredClosedLinkText"}`}
                                                        components={{ highlighted: <MprcLink color="primary.light" onClick={handleViewWhoAnswered} /> }}
                                                        values={{ count: results.answerCount }}
                                                    />
                                                : null
                                            ,
                                        ]}
                                    />
                                </Typography>
                                {allowNewAnswers && selectedAnswerId == undefined && moderateReport == null && session.user.userId === author.userId &&
                                    <MprcLink onClick={() => handleChangeView()} disabled={readOnly}>
                                        <Typography variant="caption" sx={{ textTransform: "uppercase" }}>
                                            <MprcText color="highlight">{viewMode == "toAnswer" ? t("Feed.Card.Poll.SeeAnswersAction") : t("Feed.Card.Poll.AnswerAction")}</MprcText>
                                        </Typography>
                                    </MprcLink>
                                }
                            </>
                        </Grid>
                    </Grid>
                </>
            );
};

export const DefaultFooter = () => {

    const { pub: { blocked, approvalStatus } } = useCardContext();

    if (approvalStatus != PublicationApprovalStatus.Approved)
        return <DefaultModerationFooter />;

    return (
        <Grid container justifyContent="space-between" alignItems="center">
            <Grid>
                <Grid container spacing={2} justifyContent="center" alignItems="center">
                    <Grid>
                        <LikeButton />
                    </Grid>
                    <Grid>
                        <CommentButton />
                    </Grid>
                </Grid>
            </Grid>
            {blocked?.blockedOn != null &&
                <Grid>
                    <ModeratorMessageButton optionMode="blocked" />
                </Grid>
            }
            <Grid>
                <Grid container justifyContent="center" alignItems="center">
                    <Grid>
                        <FavoriteButton />
                    </Grid>
                    <Grid>
                        <ShareButton />
                    </Grid>
                    <Grid>
                        <DefaultOptions />
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};

export const ContentPageFooter = () => {

    const { pub: { blocked } } = useCardContext();

    return (
        <Grid container justifyContent="space-between" alignItems="center">
            <Grid>
                <Grid container spacing={2} justifyContent="center" alignItems="center">
                    <Grid>
                        <LikeButton />
                    </Grid>
                </Grid>
            </Grid>
            {blocked?.blockedOn != null &&
                <Grid>
                    <ModeratorMessageButton optionMode="blocked" />
                </Grid>
            }
            <Grid>
                <Grid container justifyContent="center" alignItems="center">
                    <Grid>
                        <FavoriteButton />
                    </Grid>
                    <Grid>
                        <ShareButton />
                    </Grid>
                    <Grid>
                        <DefaultOptions />
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};
